import { ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Component, OnInit, AfterViewInit } from '@angular/core';
import * as c3 from 'c3';
import { DatePipe } from '@angular/common';
import { DataService } from '../../services/data.service';
import { GraphUtilsService } from '../../services/graph-utils.service';
import { UtilsService } from '../../services/utils.service';

@Component({
    selector: 'app-c3-batch-series',
    templateUrl: './c3-batch-series.component.html',
    styleUrls: ['./c3-batch-series.component.scss'],
    standalone: false
})
export class C3BatchSeriesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data: any;
  @Input() kpi: any;
  @Input() paramName: any;
  @Input() rule1Enabled: boolean;
  @Input() rule6Enabled: boolean;
  @Input() rule7Enabled: boolean;
  @Input() outliers: boolean;
  @Input() uslLsl: boolean;
  @Input() yzoom: any;
  @Input() redZone: boolean;
  @Input() isCapability: boolean;
  @Output() pointSelection: EventEmitter<object> = new EventEmitter();

  batchChart: any = null;

  violatedIndexes: any = {
    rule1: [],
    rule6: [],
    rule7: [],
  };

  thresholdValues: any = {
    usl: null,
    lsl: null,
    ucl: null,
    lcl: null,
    avg: null,
    avgPlus1Sigma: null,
    avgMinus1Sigma: null,
    avgPlus2Sigma: null,
    avgMinus2Sigma: null
  };

  colors: any = {};
  subscription: any;
  selectedPoints: any[] = [];

  constructor(
    private elRef: ElementRef,
    private graphUtil: GraphUtilsService,
    private dataSer: DataService,
    private datePipe: DatePipe,
    public utilSer: UtilsService,
  ) {
    this.subscription = this.dataSer.events.subscribe(this.replotGraphEventSubscription);
  }
  replotGraphEventSubscription = (res: any) => {
    if (res.name === 'replotGraph') {
      let timeoutval = 0;
      if (res.args && res.args[0] === 'menuToggle') {
        timeoutval = 101;
      }
      setTimeout(() => {
        this.checkAndReplotGraph();
      }, timeoutval);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.batchChart) {
      this.batchChart.destroy();
    }
    this.checkAndReplotGraph();
  }

  ngOnInit() {
    this.colors = this.graphUtil.getColorList();
  }

  ngAfterViewInit(): void {
    this.checkAndReplotGraph();
  }

  checkAndReplotGraph() {
    if (this.data && this.data.length && this.kpi.avg !== null) {
      this.thresholdValues = this.graphUtil.setThreshHoldsBatch(this.kpi, this.outliers, this.uslLsl, this.isCapability);
      this.violatedIndexes = this.graphUtil.populateViolatedIndexes(this.data);
      this.generateChart(this.data);
      this.selectedPoints = [];
    } else {
      this.batchChart = null;
    }
  }

  getMinMax(bounds: any, databounds: any, setpointbounds: any): void {
    if (bounds.max === undefined || bounds.max === null) {
      if (databounds.max === null) {
        bounds.max = setpointbounds.max;
      }
      if (setpointbounds.max === null) {
        bounds.max = databounds.max;
      }
    }
    if (bounds.min === undefined || bounds.min === null) {
      if (databounds.min === null) {
        bounds.min = setpointbounds.min;
      }
      if (setpointbounds.min === null) {
        bounds.min = databounds.min;
      }
    }
  }

  generateChart = (data) => {
    const databounds = this.graphUtil.findBoundaryVals(data, 'value', this.thresholdValues);
    const setpointbounds = this.graphUtil.findBoundaryVals(data, 'sp', this.thresholdValues);

    const bounds = {
      max: databounds.max >= setpointbounds.max ? databounds.max : setpointbounds.max,
      min: databounds.min <= setpointbounds.min ? databounds.min : setpointbounds.min,
    };
    this.getMinMax(bounds, databounds, setpointbounds);
    this.batchChart = c3.generate({
      bindto: this.elRef.nativeElement.children[0].children[0],
      legend: {
        show: false
      },
      padding: {
        right: 25,
        top: 10
      },
      data: {
        columns: [
          [this.paramName, ...data.map(elem => elem.value)],
          ['SET POINT', ...data.map(elem => elem.sp)]
        ],
        color: (d: any, i: c3.DataPoint) => this.colorGenerator(d, i),
        selection: {
          enabled: !this.outliers,
          multiple: true
        },
        onselected: (d) => {
          this.onPointSelect(d);
        },
        onunselected: (d) => {
          this.onPointUnSelect(d);
        }
      },
      transition: {
        duration: 0
      },
      point: {
        r: 3
      },
      axis: {
        x: {
          type: 'category',
          categories: [...data.map(elem => elem.tsEpoch === 0 ? elem.batch : this.datePipe.transform(this.utilSer.timeStampConverter(elem?.tsEpoch * 1000, false), "MMM d, y, HH:mm")), ...data.map(elem => elem.tsEpoch === 0 ? elem.batch : this.datePipe.transform(this.utilSer.timeStampConverter(elem?.tsEpoch * 1000, false), "MMM d, y, HH:mm"))],
          // categories: [...data.map(elem => this.datePipe.transform(this.utilSer.timeStampConverter(elem?.tsEpoch * 1000, false), "MMM d, y, HH:mm")),
          // ...data.map(elem => this.datePipe.transform(this.utilSer.timeStampConverter(elem?.tsEpoch * 1000, false), "MMM d, y, HH:mm"))],
          tick: {
            culling: true,
            multiline: false,
            centered: true
          },
        },
        y: {
          max: this.yzoom.max ? this.yzoom.max : bounds.max,
          min: this.yzoom.min ? this.yzoom.min : bounds.min
        }
      },
      regions: [
        {
          axis: 'y',
          end: this.thresholdValues.avgPlus3Sigma,
          start: this.thresholdValues.avgMinus3Sigma,
          class: 'control region'
        },
        {
          axis: 'y',
          end: this.thresholdValues.avgPlus2Sigma,
          start: this.thresholdValues.avgMinus2Sigma,
          class: 'average-2-sigma region'
        },
        {
          axis: 'y',
          end: this.thresholdValues.avgPlus1Sigma,
          start: this.thresholdValues.avgMinus1Sigma,
          class: 'average-1-sigma region'
        },
        {
          axis: 'y',
          end: this.thresholdValues.lsl,
          class: this.uslLsl ? this.redZone ? 'top-bottom region' : null : null
        },
        {
          axis: 'y',
          start: this.thresholdValues.usl,
          class: this.uslLsl ? this.redZone ? 'top-bottom region' : null : null
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl) ? null : this.thresholdValues.lcl,
          class: (this.redZone && this.uslLsl) ? null : 'top-bottom-fill-white region'
        },
        {
          axis: 'y',
          start: (this.redZone && this.uslLsl) ? null : this.thresholdValues.ucl,
          class: (this.redZone && this.uslLsl) ? null : 'top-bottom-fill-white region'
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl && (this.thresholdValues.lcl > this.thresholdValues.lsl) &&
            (this.thresholdValues.lcl < this.thresholdValues.usl)) ? this.thresholdValues.lcl : null,
          start: (this.redZone && this.uslLsl && (this.thresholdValues.lcl > this.thresholdValues.lsl) &&
            (this.thresholdValues.lcl < this.thresholdValues.usl)) ? this.thresholdValues.lsl : null,
          class: (this.redZone && this.uslLsl && (this.thresholdValues.lcl > this.thresholdValues.lsl) &&
            (this.thresholdValues.lcl < this.thresholdValues.usl)) ? 'top-bottom-fill-white region' : null,
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl && (this.thresholdValues.ucl < this.thresholdValues.usl) &&
            (this.thresholdValues.ucl > this.thresholdValues.lsl)) ? this.thresholdValues.usl : null,
          start: (this.redZone && this.uslLsl && (this.thresholdValues.ucl < this.thresholdValues.usl) &&
            (this.thresholdValues.ucl > this.thresholdValues.lsl)) ? this.thresholdValues.ucl : null,
          class: (this.redZone && this.uslLsl && (this.thresholdValues.ucl < this.thresholdValues.usl) &&
            (this.thresholdValues.ucl > this.thresholdValues.lsl)) ? 'top-bottom-fill-white region' : null,
        },
        {
          axis: 'y',
          end: (this.redZone && this.uslLsl && (this.thresholdValues.ucl < this.thresholdValues.lsl) &&
            (this.thresholdValues.lcl < this.thresholdValues.lsl)) ? this.thresholdValues.usl : null,
          start: (this.redZone && this.uslLsl && (this.thresholdValues.ucl < this.thresholdValues.lsl) &&
            (this.thresholdValues.lcl < this.thresholdValues.lsl)) ? this.thresholdValues.lsl : null,
          class: (this.redZone && this.uslLsl && (this.thresholdValues.ucl < this.thresholdValues.lsl) &&
            (this.thresholdValues.lcl < this.thresholdValues.lsl)) ? 'top-bottom-fill-white region' : null,
        },

      ],
      zoom: {
        type: 'scroll',
        enabled: true
      },
      grid: {
        lines: {
          front: false
        }
      },
      tooltip: {
        grouped: false,
        format: {
          title: (d: any) => this.customToolTip(d)
        }
      }
    });

    this.graphUtil.addThresholdLinesBatch(this.batchChart, this.uslLsl, this.thresholdValues);
  }

  colorGenerator(d: any, i: any) {
    let color = this.colors.normal;

    if (i && i.id) {
      if (i.id === 'SET POINT') {
        color = this.colors.setpoint;
      } else {
        if (i.index || i.index === 0) {
          if (this.violatedIndexes.rule7.indexOf(i.index) !== -1 && this.rule7Enabled) {
            color = this.colors.rule7Violation;
          }
          if (this.violatedIndexes.rule6.indexOf(i.index) !== -1 && this.rule6Enabled) {
            color = this.colors.rule6Violation;
          }
          if (this.violatedIndexes.rule1.indexOf(i.index) !== -1 && this.rule1Enabled) {
            color = this.colors.rule1Violation;
          }
        }
      }
    }

    if (this.outliers) {
      color = this.colors.normal;
    }

    return color;
  }

  onPointSelect(point) {
    if (point && (point.index || point.index === 0) && point.id !== 'SET POINT') {
      const newPoint = this.data[point.index];
      newPoint.indexInData = point.index;

      this.selectedPoints.push(newPoint);
      this.pointSelection.emit(this.selectedPoints);
    }
  }

  onPointUnSelect(point) {
    if (point && (point.index || point.index === 0) && point.id !== 'SET POINT') {
      this.selectedPoints = this.selectedPoints.filter((eachPt) => eachPt.indexInData !== point.index);
      this.pointSelection.emit(this.selectedPoints);
    }
  }

  customToolTip(d: any) {
    if (isNaN(this.data[d].convertedTS)) {
      return this.data[d].batch;
    } else {
      return this.data[d].batch + '/' + this.datePipe.transform(this.data[d].convertedTS, 'HH:mm');
    }
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

}
